import { Button, Card, CardContent, cn } from '@jeraisonne/ui';
import { useNavigate } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import type {
  AppStudentObjective,
  ObjectiveProgress,
} from '@/lib/student-objectives/objective-types';
import { objectiveCardsLabel } from '@jeraisonne/db/objectives';
import {
  computeDeadlineCountdown,
  getDeadlineBadgeClasses,
} from '@/lib/student-objectives/deadline-format';
import { ObjectiveProgressRing } from './objective-progress-ring';

interface Props {
  objectives: AppStudentObjective[];
  progressByObjectiveId: Map<string, ObjectiveProgress>;
  onRemove?: (id: string) => void;
}

export function ObjectiveList({ objectives, progressByObjectiveId, onRemove }: Props) {
  const navigate = useNavigate();

  if (objectives.length === 0) {
    return (
      <Card className="border-ink-100 border-dashed">
        <CardContent className="pb-6 pt-6 text-center">
          <p className="text-ink-400 mb-3 text-sm">Tu n'as pas encore d'objectif.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/student/objectives' })}
          >
            + Ajouter un objectif
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {objectives.map((obj) => {
        const progress = progressByObjectiveId.get(obj.id);
        return (
          <ObjectiveItem
            key={obj.id}
            objective={obj}
            progress={progress ?? null}
            onOpen={() => navigate({ to: `/student/objectives/${obj.id}` })}
            onRemove={onRemove ? () => onRemove(obj.id) : undefined}
          />
        );
      })}
    </div>
  );
}

interface ItemProps {
  objective: AppStudentObjective;
  progress: ObjectiveProgress | null;
  onOpen: () => void;
  onRemove?: () => void;
}

function ObjectiveItem({ objective, progress, onOpen, onRemove }: ItemProps) {
  const isEpreuve = objective.template.contentType === 'epreuves';
  // L'objectif Diagnostic est épinglé : ni retrait ni désactivation côté élève.
  const isPinned = objective.template.type === 'diagnostic';
  const cp = progress?.completionPercent ?? 0;
  const mp = progress?.masteryPercent ?? 0;
  const total = progress?.totalExercises ?? 0;

  const illustrationUrl = objective.template.illustrationUrl ?? null;
  const subtitle = objectivePackSubtitle(objective, progress);
  const hasIllustration = !!illustrationUrl;
  const countdown = computeDeadlineCountdown(objective.deadline);

  return (
    <Card
      className={cn(
        'border-ink-100 hover:border-primary-200 relative cursor-pointer overflow-hidden transition-colors',
      )}
      onClick={onOpen}
    >
      {hasIllustration && (
        <>
          <img
            src={illustrationUrl!}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-90"
          />
          <div
            aria-hidden="true"
            className="bg-linear-to-r from-ink-50/95 via-ink-50/70 to-ink-50/10 pointer-events-none absolute inset-0"
          />
        </>
      )}
      {countdown && (
        <span
          className={cn(
            'absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-sans text-[11px] font-semibold tabular-nums',
            getDeadlineBadgeClasses(countdown.urgency),
          )}
          aria-label={`Date butoir : ${countdown.formatted}`}
        >
          <Clock className="h-3 w-3" aria-hidden="true" />
          {countdown.formatted}
        </span>
      )}
      <CardContent className="relative pb-4 pt-4">
        <div className="flex items-start gap-3">
          <ObjectiveProgressRing completionPercent={cp} masteryPercent={mp} size="md" />
          {/*
           * `pr-28` réserve ~112px pour ne pas chevaucher le badge deadline
           * absolute `right-3 top-3` (pire cas "01M 12J 12H" = ~120px avec
           * icône). Sans ce padding, sur iPad `sm:grid-cols-2` (carte
           * ~380px), le titre est tronqué visuellement — Apple review 4
           * (rejet 2026-07-20 sur iPad Air 11 M3).
           */}
          <div className="min-w-0 flex-1 pr-28">
            <p className="text-ink-900 text-sm font-semibold leading-tight">
              {objective.template.title}
            </p>
            {subtitle && (
              <p className="text-primary-700 mt-0.5 truncate text-xs font-semibold">{subtitle}</p>
            )}
            <p className="text-ink-500 mt-0.5 text-xs">
              {isEpreuve
                ? `${progress?.engagedEpreuveCount ?? 0}/${progress?.targetEpreuveCount ?? '–'} épreuves${
                    (progress?.completedExercises ?? 0) > 0 ? ` · Maîtrise ${mp}%` : ''
                  }`
                : progress?.kind === 'cards'
                  ? `${objectiveCardsLabel(progress.cards)}${
                      (progress.cards?.card_count ?? 0) > 0 ? ` · Maîtrise ${mp}%` : ''
                    }`
                  : total > 0
                    ? `${progress?.completedExercises ?? 0}/${total} exercices · Maîtrise ${mp}%`
                    : 'Chargement…'}
            </p>
          </div>
        </div>
        <div className="border-ink-100/70 mt-3 flex items-center justify-between border-t pt-3">
          <Button variant="ghost" size="sm" onClick={onOpen}>
            Continuer →
          </Button>
          {onRemove && !isPinned && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="text-ink-400 text-xs transition-colors hover:text-red-500"
            >
              Retirer
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Sous-titre de la carte d'objectif : le ou les packs du périmètre, ou une
 * invitation à choisir son pack (« Réviser un pack » pas encore choisi).
 */
export function objectivePackSubtitle(
  objective: AppStudentObjective,
  progress: ObjectiveProgress | null,
): string | null {
  if (progress?.kind !== 'cards') return null;
  if (objective.template.requiresPackSelection && !objective.selectedPackId) {
    return 'Choisis ton pack';
  }
  const titles = progress.packTitles ?? [];
  if (titles.length === 0) return null;
  if (titles.length <= 2) return titles.join(' · ');
  return `${titles.length} packs`;
}
